// src/Pages/Shop/Checkout.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
import { Parallax } from 'react-scroll-parallax';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import { Loader2, CheckCircle, AlertCircle, Shield, CreditCard, Building2 } from 'lucide-react';
import axios from 'axios';
import config from '../../config';
import { fadeIn } from '../../Functions/GlobalAnimations';

const Checkout = (props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [basket, setBasket] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('virement');
  const [virementInstructions, setVirementInstructions] = useState(null);

  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const isRtl = i18n.language === 'ar';

  // Calcul des informations d'abonnement
  const subscriptionInfo = useMemo(() => {
    if (!activeSubscription) return null;
    
    const remainingDocs = activeSubscription.number_document || 0;
    const basketCount = basket.length;
    const willCover = remainingDocs >= basketCount;
    const remainingAfter = willCover ? remainingDocs - basketCount : remainingDocs;
    const willPay = willCover ? 0 : basket.reduce((sum, item) => sum + (item.price_ttc || 0), 0);
    
    return {
      remainingDocs,
      basketCount,
      willCover,
      remainingAfter,
      willPay,
      subscriptionCode: activeSubscription.code_subscription,
      endDate: activeSubscription.date_fin_subscription,
      isActive: activeSubscription.isActive && activeSubscription.etat_subscription === 'en cours'
    };
  }, [activeSubscription, basket]);

  // Méthodes de paiement (cartes stylées)
  const PAYMENT_METHODS = [
    {
      id: 'flouci',
      name: t('checkout.flouci_option'),
      subtitle: t('checkout.flouci_subtitle'),
      img: '/flouci.png',
      disabled: false,
      features: ['💳 Cartes Visa/MasterCard', '📱 Portefeuille Flouci', '⚡ Paiement sécurisé'],
    },
    {
      id: 'virement',
      name: t('checkout.virement_option'),
      subtitle: t('checkout.virement_subtitle'),
      img: '/virement_bank.png',
      disabled: false,
      features: ['🏦 Virement bancaire', '📄 Reçu à envoyer', '⏱ 24-48h de traitement'],
    },
    // Ajoute Konnect si tu veux
    // {
    //   id: 'konnect',
    //   name: t('checkout.konnect_option') || 'Konnect',
    //   subtitle: 'Paiement mobile rapide',
    //   img: '/konnect.png',
    // },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast.error(t('checkout.errors.login_required'));
        navigate('/login');
        return;
      }

      try {
        // Récupérer l'utilisateur
        const userRes = await axios.get(`${config.SITE_API_URL}/user-profile`, {
          headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        });
        setUser(userRes.data.customer);

        // Récupérer le panier
        const basketRes = await axios.get(`${config.SITE_API_URL}/get-basket`, {
          headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        });
        
        if (basketRes.data.basket?.length === 0) {
          toast.error(t('checkout.errors.empty_basket'));
          setTimeout(() => navigate('/shopping-cart'), 2000);
          return;
        }
        setBasket(basketRes.data.basket || []);

        // Récupérer l'abonnement actif
        const subRes = await axios.get(`${config.SITE_API_URL}/getactivesubscriptionbyuser`, {
          headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        });
        
        const activeSub = subRes.data?.[0] || null;
        setActiveSubscription(activeSub);

      } catch (error) {
        toast.error(error.response?.data?.message || t('checkout.errors.data_fetch_error'));
        setTimeout(() => navigate('/shopping-cart'), 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, t]);

  const handleCheckout = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token || !user) return;

    setIsRedirecting(true);

    try {
      const res = await axios.post(
        `${config.SITE_API_URL}/checkout`,
        { userId: user._id, paymentMethod: selectedPayment },
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );

      const { order, payUrl, virementInstructions: instructions } = res.data;

      if (payUrl) {
        toast.info(t('checkout.redirecting'));
        setTimeout(() => window.location.href = payUrl, 1200);
        return;
      }

      if (selectedPayment === 'virement') {
        setVirementInstructions(instructions || 
          t('checkout.virement_pending') + ` Référence : ${order?.code_orders || '—'}`);
        toast.success(t('checkout.virement_pending'));
        setTimeout(() => navigate(`/payment-pending?orderId=${order?._id}`), 2000);
      } else {
        toast.success(t('checkout.order_success'));
        setTimeout(() => navigate('/payment-success'), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('checkout.errors.checkout_error'));
    } finally {
      setIsRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ direction }}>
        <Loader2 className="animate-spin mr-3" size={32} />
        <p className="text-xl">{t('checkout.loading')}</p>
      </div>
    );
  }

  if (!user || basket.length === 0) return null;

  const totalDocuments = basket.length;
  const totalPrice = basket.reduce((sum, item) => sum + (item.price_ttc || 0), 0);
  const finalTotal = subscriptionInfo?.willCover ? 0 : totalPrice;

  return (
    <div className="min-h-screen bg-gray-50" style={{ ...props.style, direction }}>
      {/* Parallax Header */}
      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
        <Parallax
          className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
          translateY={[-40, 40]}
          style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
        />
        <Container className="h-full relative">
          <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
            <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
              <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
                {t('checkout.complete_purchase')}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>

      <main className="max-w-7xl mx-auto px-4 min-sm:px-6 min-lg:px-8 py-12 min-md:py-16">
        <div className="grid min-lg:grid-cols-3 gap-8 xl:gap-10">
          {/* Résumé commande */}
          <div className="min-lg:col-span-1 order-2 min-lg:order-1">
            <div
              className="rounded-2xl p-7 min-md:p-9 shadow-xl sticky top-8"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                borderLeft: '5px solid #f29f2f',
              }}
            >
              <h3 className="text-xlg font-bold mb-7 flex items-center gap-3" style={{ color: '#003479' }}>
                <span className="text-xxxl">📋</span>
                {t('checkout.order_summary')}
              </h3>

              {/* Carte d'abonnement actif */}
              {subscriptionInfo && subscriptionInfo.isActive && (
                <div className={`mb-6 p-5 rounded-xl ${subscriptionInfo.willCover ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-start gap-3">
                    {subscriptionInfo.willCover ? (
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-sm mb-2" style={{ color: subscriptionInfo.willCover ? '#166534' : '#854d0e' }}>
                        {t('checkout.active_subscription')}
                      </p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('checkout.remaining_documents')}:</span>
                          <strong className={subscriptionInfo.willCover ? 'text-green-700' : 'text-yellow-700'}>
                            {subscriptionInfo.remainingDocs}
                          </strong>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('checkout.documents_in_cart')}:</span>
                          <strong className={subscriptionInfo.willCover ? 'text-green-700' : 'text-red-600'}>
                            {subscriptionInfo.basketCount}
                          </strong>
                        </div>

                        {subscriptionInfo.willCover ? (
                          <>
                            <div className="flex justify-between pt-2 border-t border-green-200">
                              <span className="text-gray-600">{t('checkout.remaining_after_purchase')}:</span>
                              <strong className="text-green-700 text-lg">
                                {subscriptionInfo.remainingAfter}
                              </strong>
                            </div>
                            <div className="mt-3 p-2 bg-green-100 rounded-lg text-center">
                              <span className="text-green-800 font-medium text-xs">
                                ✓ {t('checkout.free_with_subscription')}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="mt-2 p-2 bg-red-50 rounded-lg">
                            <p className="text-red-700 text-xs">
                              ⚠️ {t('checkout.subscription_insufficient', { 
                                remaining: subscriptionInfo.remainingDocs, 
                                needed: subscriptionInfo.basketCount 
                              })}
                            </p>
                          </div>
                        )}
                      </div>

                      {subscriptionInfo.endDate && (
                        <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200">
                          📅 {t('checkout.valid_until')}: {new Date(subscriptionInfo.endDate).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Détails des prix */}
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">
                    {t('checkout.documents_count')}
                  </span>
                  <span className="font-semibold" style={{ color: '#0879bd' }}>
                    {totalDocuments} 
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">
                    {t('checkout.documents_price', { count: totalDocuments })}
                  </span>
                  <span className="font-bold" style={{ color: '#0879bd' }}>
                    {totalPrice.toFixed(3)} {t('DT')}
                  </span>
                </div>

                {!subscriptionInfo?.willCover &&(
                  <div className="flex justify-between text-xl font-bold pt-4 border-t border-gray-300" style={{ color: '#003479' }}>
                  <span>{t('checkout.total_to_pay')}</span>
                  <span>{finalTotal.toFixed(3)} {t('DT')}</span>
                </div>)}
              </div>
            </div>
          </div>

          {/* Infos utilisateur + méthodes paiement */}
          <div className="min-lg:col-span-2 order-1 min-lg:order-2">
            <div className="bg-white rounded-2xl shadow-xl p-7 min-md:p-10">
              {/* Infos utilisateur */}
              <h3 className="text-xlg font-bold mb-7 flex items-center gap-3" style={{ color: '#003479' }}>
                <span className="text-xxxl">👤</span>
                {t('checkout.user_info')}
              </h3>

              <div className="grid grid-cols-1 min-md:grid-cols-2 gap-6 mb-12">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.first_name')}
                  </label>
                  <input
                    type="text"
                    value={user.first_name_parent || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.last_name')}
                  </label>
                  <input
                    type="text"
                    value={user.last_name_parent || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.phone')}
                  </label>
                  <input
                    type="text"
                    value={user.parent_phone || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('checkout.adresse')}
                  </label>
                  <input
                    type="text"
                    value={user.adresse?.replace("Governorate", "") || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800"
                  />
                </div>
              </div>

              {/* Méthodes de paiement - cachées si l'abonnement couvre tout */}
              {(!subscriptionInfo?.willCover) && (
                <>
                  <h3 className="text-xlg font-bold mb-7 flex items-center gap-3" style={{ color: '#003479' }}>
                    <span className="text-xxxl">💳</span>
                    {t('checkout.payment_method')}
                  </h3>

                  <div className="grid grid-cols-1 min-sm:grid-cols-2 gap-5 mb-10">
                    {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => {
                      if (!method.disabled) {
                        setSelectedPayment(method.id);
                      }
                    }}
                    className={`relative p-6 border-2 rounded-2xl transition-all duration-300 hover:shadow-md hover:border-[#0879bd] group ${
                      selectedPayment === method.id
                        ? 'border-[#0879bd] bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white'
                    }
                    ${method.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-[#0879bd] group'}`}
                    //Si flouci fonction changer etat de paiement disabled : flase 
                    // et supprimer cet ligne dans classname ${method.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-[#0879bd] group'}`}
                    disabled={method.disabled}
                  >
                    {selectedPayment === method.id && !method.disabled && (
                      <div
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold shadow"
                        style={{ backgroundColor: '#f29f2f' }}
                      >
                        ✓
                      </div>
                    )}
                    
                    {method.disabled && (
                      <div
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold shadow bg-gray-400"
                      >
                        ✗
                      </div>
                    )}
                    <img
                      src={method.img}
                      alt={method.name}
                      className="h-16 mx-auto mb-4 object-contain"
                    />
                    <p className={`font-semibold text-center text-lg mb-1 ${method.disabled ? 'text-gray-500' : ''}`}>
                      {method.name}
                      {method.etat && (
                        <span className={`block text-sm mt-1 ${method.disabled ? 'text-red-500' : 'text-gray-500'}`}>
                          {method.etat}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-center text-gray-500">{method.subtitle}</p>
                    
                    {method.disabled && (
                      <div className="mt-3 p-2 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 font-medium">
                          {method.message || t('checkout_subscription.unavailable')}
                        </p>
                      </div>
                    )}
                  </button>
                ))}
                  </div>
                </>
              )}

              {/* Bouton final */}
              <button
                onClick={handleCheckout}
                disabled={isRedirecting}
                className={`w-full py-4 rounded-2xl font-bold text-lg text-white transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] ${
                  isRedirecting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                style={{
                  backgroundColor: subscriptionInfo?.willCover ? '#10b981' : '#f29f2f',
                  boxShadow: subscriptionInfo?.willCover 
                    ? '0 10px 30px rgba(16, 185, 129, 0.25)' 
                    : '0 10px 30px rgba(242, 159, 47, 0.25)',
                }}
              >
                {isRedirecting ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={20} />
                    {t('checkout.redirecting')}
                  </div>
                ) : subscriptionInfo?.willCover ? (
                  t('checkout.confirm_with_subscription')
                ) : (
                  `${t('checkout.finalize')} ${finalTotal.toFixed(3)} ${t('DT')}`
                )}
              </button>

              {/* Instructions virement */}
              {virementInstructions && (
                <div className="mt-10 p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <h4 className="text-lg font-bold mb-3" style={{ color: '#f29f2f' }}>
                    {t('checkout.virement_pending')}
                  </h4>
                  <p className="text-gray-700 whitespace-pre-line">
                    {virementInstructions}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <ToastContainer position={isRtl ? 'bottom-right' : 'bottom-left'} autoClose={3000} />
    </div>
  );
};

export default Checkout;